import type { PessoaDoPainel } from "@/api/contrato";
import { TAMANHO_DA_PAGINA, useAlterarPessoa, useBancos, usePaginaDePessoas } from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { CampoDeBusca } from "@/componentes/CampoDeBusca";
import { ChipsDeFiltro } from "@/componentes/ChipsDeFiltro";
import { Dica } from "@/componentes/Dica";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { MenuDeLinha, type ItemDoMenu } from "@/componentes/MenuDeLinha";
import { Paginacao } from "@/componentes/Paginacao";
import { Selecao } from "@/componentes/Selecao";
import { Tabela, type Coluna } from "@/componentes/Tabela";
import { useAlternarSituacao } from "@/componentes/useAlternarSituacao";
import {
  useErroDaPagina,
  useFiltrosNaUrl,
  type DefinicaoDeFiltro,
} from "@/componentes/useFiltrosNaUrl";
import { banco, documento, quantidade } from "@/formato";
import { rotaDoCedente } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { gruposDeBancos } from "./bancos";
import { ROTULO_DA_PENDENCIA } from "./pendencias";
import { textoDaRecusa } from "./recusas";
import { SeloDoCedente } from "./SeloDoCedente";

const FILTROS: readonly DefinicaoDeFiltro<"q" | "banco">[] = [
  { chave: "q", rotulo: "Busca" },
  { chave: "banco", rotulo: "Banco", textoDoValor: banco },
];

/**
 * A página da lista, a partir da URL. O cabeçalho de `Cedentes` lê a mesma consulta para
 * a contagem: a chave é a mesma, e o TanStack Query entrega uma requisição só.
 */
export function useListaDeCedentes() {
  const { cliente, estado } = useSessao();
  const filtros = useFiltrosNaUrl(FILTROS);
  const consulta = usePaginaDePessoas(
    cliente,
    { ...filtros.valores, cursor: filtros.cursor, limite: TAMANHO_DA_PAGINA },
    estado.fase === "dentro",
  );

  return { filtros, consulta };
}

/** O filtro de banco das duas listas, pelo catálogo: Sicoob e Sicredi primeiro. */
export function FiltroDeBanco({
  valor,
  aoMudar,
}: {
  valor: string | undefined;
  aoMudar: (valor: string) => void;
}) {
  const { cliente, estado } = useSessao();
  const { data } = useBancos(cliente, estado.fase === "dentro");

  return (
    <Selecao
      rotulo="Banco"
      opcoes={[["", "Todos"]]}
      grupos={gruposDeBancos(data?.bancos ?? [])}
      value={valor ?? ""}
      onChange={(evento) => aoMudar(evento.target.value)}
      // O rótulo dos bancos é longo: sem largura, o `select` ocupa o tamanho dele.
      className="h-8! w-56"
    />
  );
}

/*
 * A porta de entrada do cadastro (spec "A lista de cedentes é a porta de entrada"): a
 * situação vem calculada pelo servidor, e a linha é link real. A estrutura é a da GUI da
 * TecnoSpeed, com busca e filtro que ela não tem.
 */
export function ListaDeCedentes() {
  const { filtros, consulta } = useListaDeCedentes();
  const { data, error, isPending } = consulta;
  const { erro, recuperando } = useErroDaPagina(error);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <CampoDeBusca
          rotulo="Buscar cedente"
          placeholder="Razão social ou documento"
          valor={filtros.valores.q}
          aoMudar={(valor) => filtros.ajustar("q", valor)}
        />
        <FiltroDeBanco
          valor={filtros.valores.banco}
          aoMudar={(valor) => filtros.ajustar("banco", valor)}
        />
      </div>

      <ChipsDeFiltro filtros={filtros} />

      {erro !== null ? (
        <FaixaDeErro erro={erro} />
      ) : (
        <>
          <Tabela
            rotulo="Cedentes"
            colunas={COLUNAS}
            itens={data?.pessoas ?? []}
            carregando={isPending || recuperando}
            chaveDe={(pessoa) => pessoa.id}
            hrefDe={(pessoa) => rotaDoCedente(pessoa.id)}
            rotuloDe={(pessoa) => pessoa.razao_social}
            vazio="Nenhum cedente."
          />

          {data !== undefined && (
            <Paginacao
              itens={data.pessoas.length}
              total={data.total}
              proximoCursor={data.proximo_cursor}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * O selo, e o motivo em tooltip. A `Dica` sobe acima da camada do link da linha, e o
 * selo fica embaixo dela: clicar nele continua abrindo o cedente.
 */
function SituacaoDoCedente({ pessoa }: { pessoa: PessoaDoPainel }) {
  const { pronto, bloqueantes, primeira_pendencia: primeira } = pessoa.prontidao;

  return (
    <span className="inline-flex items-center gap-1">
      <SeloDoCedente status={pessoa.status} pronto={pronto} bloqueantes={bloqueantes} />
      {primeira !== null && pessoa.status !== "bloqueado" && (
        <span className="relative z-[1] inline-flex">
          <Dica texto={ROTULO_DA_PENDENCIA[primeira]} />
        </span>
      )}
    </span>
  );
}

function AcoesDoCedente({ pessoa }: { pessoa: PessoaDoPainel }) {
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();
  const alterar = useAlterarPessoa(cliente, pessoa.id);
  const ehAdmin = pode("admin");
  const ligado = pessoa.status !== "bloqueado";

  const situacao = useAlternarSituacao({
    ligado,
    desligar: {
      titulo: `Bloquear ${pessoa.razao_social}?`,
      corpo: "A emissão passa a recusar boletos deste cedente.",
      verbo: "Bloquear cedente",
    },
    ligar: {
      titulo: `Reativar ${pessoa.razao_social}?`,
      corpo: "A emissão volta a aceitar boletos deste cedente.",
      verbo: "Reativar cedente",
    },
    aoAlterar: async (ligar, motivo) => {
      try {
        await alterar.mutateAsync(ligar ? { status: "ativo" } : { status: "bloqueado", motivo });
      } catch (falha) {
        // A confirmação mostra a mensagem do erro: aqui ela já vai em texto de tela.
        throw new Error(textoDaRecusa(falha), { cause: falha });
      }
      avisos.feito(ligar ? "Cedente reativado" : "Cedente bloqueado");
    },
  });

  const itens: ItemDoMenu[] = [
    ...(ehAdmin
      ? ([
          {
            tipo: "link",
            rotulo: "Editar",
            para: rotaDoCedente(pessoa.id, "dados", { abrir: "editar" }),
          },
        ] as const)
      : []),
    { tipo: "link", rotulo: "Contas", para: rotaDoCedente(pessoa.id, "contas") },
    ...(ehAdmin
      ? ([{ tipo: "interruptor", rotulo: "Ativo", ligado, aoAlternar: situacao.pedir }] as const)
      : []),
  ];

  return (
    <>
      <MenuDeLinha rotulo={`Ações de ${pessoa.razao_social}`} itens={itens} />
      {situacao.dialogo}
    </>
  );
}

/*
 * `176px` é `pronto para emitir` com a dica. Em 1024 saem as contagens, que a página do
 * cedente mostra com o detalhe, e depois os bancos, que estão no subtítulo dela.
 */
const COLUNAS: readonly Coluna<PessoaDoPainel>[] = [
  {
    chave: "situacao",
    rotulo: "Situação",
    largura: "176px",
    render: (pessoa) => <SituacaoDoCedente pessoa={pessoa} />,
  },
  {
    chave: "razao",
    rotulo: "Razão social",
    largura: "minmax(180px, 2fr)",
    render: (pessoa) => (
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate">{pessoa.razao_social}</span>
        {pessoa.nome_fantasia !== undefined && (
          <span className="truncate text-[11px] text-texto-3">{pessoa.nome_fantasia}</span>
        )}
      </div>
    ),
  },
  {
    chave: "documento",
    rotulo: "Documento",
    largura: "148px",
    render: (pessoa) => <span className="dados text-texto-2">{documento(pessoa.documento)}</span>,
  },
  {
    chave: "bancos",
    rotulo: "Bancos",
    largura: "minmax(100px, 1fr)",
    descarte: 2,
    render: (pessoa) => (
      <span className="text-texto-2">
        {pessoa.bancos.length > 0 ? pessoa.bancos.map(banco).join(", ") : "—"}
      </span>
    ),
  },
  {
    chave: "contas",
    rotulo: "Contas",
    largura: "64px",
    descarte: 1,
    direita: true,
    render: (pessoa) => (
      <span className="tabular text-texto-2">{quantidade(pessoa.contas ?? 0)}</span>
    ),
  },
  {
    chave: "convenios",
    rotulo: "Convênios",
    largura: "84px",
    descarte: 1,
    direita: true,
    render: (pessoa) => (
      <span className="tabular text-texto-2">{quantidade(pessoa.convenios ?? 0)}</span>
    ),
  },
  {
    chave: "acoes",
    rotulo: "Ações",
    largura: "56px",
    direita: true,
    interativa: true,
    render: (pessoa) => <AcoesDoCedente pessoa={pessoa} />,
  },
];
